import React, { useState, useEffect } from 'react';
import { Info, Cpu, Globe, Clock, Server, CheckCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const SystemInfo = () => {
    const [backendInfo, setBackendInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Frontend Info from Vite define
    const frontendVersion = import.meta.env.VITE_APP_VERSION || '1.0.0';
    const buildTime = import.meta.env.VITE_BUILD_TIME;

    useEffect(() => {
        const fetchBackendInfo = async () => {
            try {
                // We use the context path from vite proxy or direct URL
                // The actuator endpoint is usually at /yaseero-backend/actuator/info
                const response = await axios.get('/yaseero-backend/actuator/info');
                setBackendInfo(response.data);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch backend info:', err);
                setError('Could not connect to backend info service');
                setLoading(false);
            }
        };

        fetchBackendInfo();
    }, []);

    const InfoCard = ({ icon: Icon, title, value, subValue, statusColor = 'text-blue-600' }) => (
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow duration-200">
            <div className="flex items-start gap-4">
                <div className={`p-3 rounded-xl bg-gray-50 ${statusColor}`}>
                    <Icon size={24} />
                </div>
                <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</h4>
                    <p className="text-xl font-bold text-gray-900 mt-1">{value}</p>
                    {subValue && <p className="text-xs text-gray-400 mt-1 font-mono">{subValue}</p>}
                </div>
            </div>
        </div>
    );

    return (
        <div className="p-6 max-w-5xl mx-auto animate-fadeIn">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                        <Info className="text-blue-600" />
                        System Information
                    </h2>
                    <p className="text-gray-500 mt-1">Detailed build and environment information for monitoring.</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 text-emerald-600 rounded-full text-sm font-medium border border-emerald-100">
                    <CheckCircle size={14} />
                    System Active
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {/* Frontend Section */}
                <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Globe size={20} className="text-purple-600" />
                        Web Application (Frontend)
                    </h3>
                    <InfoCard 
                        icon={Globe} 
                        title="Web Version" 
                        value={frontendVersion} 
                        statusColor="text-purple-600"
                    />
                    <InfoCard 
                        icon={Clock} 
                        title="Last Build" 
                        value={buildTime ? new Date(buildTime).toLocaleString() : 'N/A'} 
                        subValue={buildTime}
                        statusColor="text-purple-600"
                    />
                </div>

                {/* Backend Section */}
                <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                        <Server size={20} className="text-blue-600" />
                        Core Service (Backend)
                    </h3>
                    {loading ? (
                        <div className="bg-white p-10 rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center text-gray-400">
                            <div className="w-8 h-8 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin mb-3" />
                            <p>Fetching backend info...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 p-6 rounded-2xl border border-red-100 flex items-start gap-4 text-red-700">
                            <AlertTriangle className="mt-1" />
                            <div>
                                <p className="font-bold">Backend Offline or Unreachable</p>
                                <p className="text-sm opacity-80">{error}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <InfoCard 
                                icon={Cpu} 
                                title="Core Version" 
                                value={backendInfo?.build?.version || 'Unknown'} 
                                statusColor="text-blue-600"
                            />
                            <InfoCard 
                                icon={Clock} 
                                title="Build Time" 
                                value={backendInfo?.build?.time ? new Date(backendInfo.build.time).toLocaleString() : 'N/A'} 
                                subValue={backendInfo?.build?.time}
                                statusColor="text-blue-600"
                            />
                        </>
                    )}
                </div>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6">
                <div className="flex gap-4">
                    <div className="p-2 bg-white rounded-lg shadow-sm text-blue-600 h-fit">
                        <Info size={20} />
                    </div>
                    <div>
                        <h4 className="font-bold text-blue-900">About Build Info</h4>
                        <p className="text-sm text-blue-800/80 mt-1 leading-relaxed">
                            This information is automatically generated during the CI/CD pipeline. 
                            It helps developers and administrators identify which version of the software is 
                            currently running in this environment, ensuring alignment between the 
                            Web interface and the Core processing engine.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemInfo;
